import React, { useEffect, useState } from "react";

import Button from "ui/Button";
import {
  ViewPermission,
  ModifyPermission,
  BillingPermission,
  APIPermission,
  ManagePermission,
  WorkbooksPermission,
} from "ui/DocsSnippet";
import FormTextField from "ui/FormTextField";
import Loading from "ui/Loading";

import styles from "./style.module.scss";
import { OrganizationPermission } from "types/types";
import { RoleFormQuery, useRoleFormQuery } from "./gql/Query.generated";

type PermissionType = Pick<
  OrganizationPermission,
  | "id"
  | "databaseId"
  | "serverId"
  | "serverGroupId"
  | "view"
  | "modify"
  | "billing"
  | "api"
  | "manage"
  | "createWorkbooks"
>;

type DatabaseType = RoleFormQuery["getDatabases"][number];
type ServerType = RoleFormQuery["getServers"][number];
type ServerGroupType = RoleFormQuery["getServerGroups"][number];

type PolicyScope = "unrestricted" | "server" | "serverGroup" | "database";

type Props = {
  organizationSlug: string;
  roleName?: string;
  permissions?: PermissionType[];
};

function deriveScopeFromPermission(permission: PermissionType): PolicyScope {
  return permission.serverId
    ? "server"
    : permission.serverGroupId
    ? "serverGroup"
    : permission.databaseId
    ? "database"
    : "unrestricted";
}

function deriveResourceId(permission: PermissionType): string {
  return (
    permission.serverId ||
    permission.serverGroupId ||
    permission.databaseId ||
    ""
  );
}

function RoleForm({ organizationSlug, roleName, permissions = [] }: Props) {
  const { data, loading, error } = useRoleFormQuery({
    variables: { organizationSlug },
  });

  const [newPermissions, setNewPermissions] = useState<PermissionType[]>([]);
  const [newPermissionIds, setNewPermissionIds] = useState<string[]>([]);
  const [deletedPermissionIds, setDeletedPermissionIds] = useState<string[]>(
    [],
  );

  const [scopes, setScopes] = useState<Record<string, PolicyScope>>(() => {
    const result: Record<string, PolicyScope> = {};
    for (const p of permissions) {
      result[p.id] = deriveScopeFromPermission(p);
    }
    return result;
  });

  const [resourceIds, setResourceIds] = useState<Record<string, string>>(() => {
    const result: Record<string, string> = {};
    for (const p of permissions) {
      result[p.id] = deriveResourceId(p);
    }
    return result;
  });

  useEffect(() => {
    if (permissions.length === 0) {
      const newPermission: PermissionType = {
        id: "new-0",
        databaseId: null,
        serverId: null,
        serverGroupId: null,
        view: false,
        modify: false,
        billing: false,
        api: false,
        manage: false,
        createWorkbooks: false,
      };
      setNewPermissions([newPermission]);
      setNewPermissionIds([newPermission.id]);
      setScopes((prev) => ({ ...prev, [newPermission.id]: "unrestricted" }));
    }
  }, [permissions.length]);

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const { getDatabases, getServerGroups, getServers } = data;

  function addPermission() {
    const newPermission: PermissionType = {
      id: "new-" + newPermissions.length,
      databaseId: null,
      serverId: null,
      serverGroupId: null,
      view: false,
      modify: false,
      billing: false,
      api: false,
      manage: false,
      createWorkbooks: false,
    };

    setNewPermissions((prev) => prev.concat([newPermission]));
    setNewPermissionIds((prev) => prev.concat([newPermission.id]));
    setScopes((prev) => ({ ...prev, [newPermission.id]: "unrestricted" }));
  }

  function deletePermission(permissionId: string) {
    setDeletedPermissionIds((prev) => prev.concat([permissionId]));
  }

  function handleScopeChange(permissionId: string, newScope: PolicyScope) {
    setScopes((prev) => ({ ...prev, [permissionId]: newScope }));
    setResourceIds((prev) => ({ ...prev, [permissionId]: "" }));
  }

  function resourceFieldName(permissionId: string): string {
    const scope = scopes[permissionId] || "unrestricted";
    switch (scope) {
      case "server":
        return `role[permissions_attributes][${permissionId}][server_id]`;
      case "serverGroup":
        return `role[permissions_attributes][${permissionId}][server_group_id]`;
      case "database":
        return `role[permissions_attributes][${permissionId}][database_id]`;
      default:
        return "";
    }
  }

  function renderResourceOptions(scope: PolicyScope): React.ReactNode {
    switch (scope) {
      case "server":
        return getServers.map(
          (s: ServerType): React.ReactNode => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ),
        );
      case "serverGroup":
        return getServerGroups.map(
          (sg: ServerGroupType): React.ReactNode => (
            <option key={sg.id} value={sg.id}>
              {sg.name}
            </option>
          ),
        );
      case "database":
        return getDatabases.map(
          (d: DatabaseType): React.ReactNode => (
            <option key={d.id} value={d.id}>
              {d.displayName}
            </option>
          ),
        );
      default:
        return null;
    }
  }

  function renderPermission(permission: PermissionType) {
    if (deletedPermissionIds.indexOf(permission.id) !== -1) {
      return null;
    }

    const isNew = newPermissionIds.indexOf(permission.id) !== -1;

    const scope = scopes[permission.id] || "unrestricted";

    return (
      <tr key={permission.id}>
        <td className={styles.restriction}>
          <select
            id={`role_permissions_attributes_${permission.id}_scope`}
            className="form-control"
            value={scope}
            onChange={(evt) => {
              handleScopeChange(
                permission.id,
                evt.currentTarget.value as PolicyScope,
              );
            }}
          >
            <option value="unrestricted">Unrestricted</option>
            {getServers.length > 0 && <option value="server">Server</option>}
            {getServerGroups.length > 0 && (
              <option value="serverGroup">Server Group</option>
            )}
            {getDatabases.length > 0 && (
              <option value="database">Database</option>
            )}
          </select>
        </td>
        <td className={styles.restriction}>
          {scope === "unrestricted" ? (
            <>
              <select className="form-control" disabled>
                <option value="">All</option>
              </select>
              <input
                type="hidden"
                name={`role[permissions_attributes][${permission.id}][server_id]`}
                value=""
              />
              <input
                type="hidden"
                name={`role[permissions_attributes][${permission.id}][server_group_id]`}
                value=""
              />
              <input
                type="hidden"
                name={`role[permissions_attributes][${permission.id}][database_id]`}
                value=""
              />
            </>
          ) : (
            <>
              <select
                id={`role_permissions_attributes_${permission.id}_resource`}
                name={resourceFieldName(permission.id)}
                className="form-control"
                value={resourceIds[permission.id] || ""}
                onChange={(evt) => {
                  const value = evt.currentTarget.value;
                  setResourceIds((prev) => ({
                    ...prev,
                    [permission.id]: value,
                  }));
                }}
              >
                <option value="">All</option>
                {renderResourceOptions(scope)}
              </select>
              {scope !== "server" && (
                <input
                  type="hidden"
                  name={`role[permissions_attributes][${permission.id}][server_id]`}
                  value=""
                />
              )}
              {scope !== "serverGroup" && (
                <input
                  type="hidden"
                  name={`role[permissions_attributes][${permission.id}][server_group_id]`}
                  value=""
                />
              )}
              {scope !== "database" && (
                <input
                  type="hidden"
                  name={`role[permissions_attributes][${permission.id}][database_id]`}
                  value=""
                />
              )}
            </>
          )}
        </td>
        <td className={styles.permission}>
          <input
            name={`role[permissions_attributes][${permission.id}][view]`}
            type="hidden"
            value="0"
          />
          <input
            name={`role[permissions_attributes][${permission.id}][view]`}
            id={`role_permissions_attributes_${permission.id}_view`}
            type="checkbox"
            value="1"
            defaultChecked={permission.view}
          />
        </td>
        <td className={styles.permission}>
          <input
            name={`role[permissions_attributes][${permission.id}][modify]`}
            type="hidden"
            value="0"
          />
          <input
            name={`role[permissions_attributes][${permission.id}][modify]`}
            id={`role_permissions_attributes_${permission.id}_modify`}
            type="checkbox"
            value="1"
            defaultChecked={permission.modify}
          />
        </td>
        <td className={styles.permission}>
          <input
            name={`role[permissions_attributes][${permission.id}][create_workbooks]`}
            type="hidden"
            value="0"
          />
          <input
            name={`role[permissions_attributes][${permission.id}][create_workbooks]`}
            id={`role_permissions_attributes_${permission.id}_create_workbooks`}
            type="checkbox"
            value="1"
            defaultChecked={permission.createWorkbooks}
          />
        </td>
        <td className={styles.permission}>
          <input
            name={`role[permissions_attributes][${permission.id}][billing]`}
            type="hidden"
            value="0"
          />
          {scope === "unrestricted" && (
            <input
              name={`role[permissions_attributes][${permission.id}][billing]`}
              id={`role_permissions_attributes_${permission.id}_billing`}
              type="checkbox"
              value="1"
              defaultChecked={permission.billing}
            />
          )}
        </td>
        <td className={styles.permission}>
          <input
            name={`role[permissions_attributes][${permission.id}][api]`}
            type="hidden"
            value="0"
          />
          {scope === "unrestricted" && (
            <input
              name={`role[permissions_attributes][${permission.id}][api]`}
              id={`role_permissions_attributes_${permission.id}_api`}
              type="checkbox"
              value="1"
              defaultChecked={permission.api}
            />
          )}
        </td>
        <td className={styles.permission}>
          <input
            name={`role[permissions_attributes][${permission.id}][manage]`}
            type="hidden"
            value="0"
          />
          {scope === "unrestricted" && (
            <input
              name={`role[permissions_attributes][${permission.id}][manage]`}
              id={`role_permissions_attributes_${permission.id}_manage`}
              type="checkbox"
              value="1"
              defaultChecked={permission.manage}
            />
          )}
        </td>
        <td>
          {!isNew && (
            <input
              type="hidden"
              name={`role[permissions_attributes][${permission.id}][id]`}
              value={permission.id}
            />
          )}
          <Button
            bare
            variant="link"
            type="button"
            onClick={() => {
              deletePermission(permission.id);
            }}
          >
            Delete
          </Button>
        </td>
      </tr>
    );
  }

  function renderDeletedPermission(permissionId: string): React.ReactNode {
    if (newPermissionIds.indexOf(permissionId) !== -1) {
      return null;
    }

    return (
      <span key={permissionId}>
        <input
          type="hidden"
          name={`role[permissions_attributes][${permissionId}][id]`}
          value={permissionId}
        />
        <input
          type="hidden"
          name={`role[permissions_attributes][${permissionId}][_destroy]`}
          value="1"
        />
      </span>
    );
  }

  return (
    <div className={styles.container}>
      <FormTextField
        name="role[name]"
        required
        defaultValue={roleName}
        label="Name"
      />
      <table>
        <thead>
          <tr>
            <th className={styles.restriction}>Scope</th>
            <th className={styles.restriction}>Resource</th>
            <th className={styles.permission}>
              View <ViewPermission />
            </th>
            <th className={styles.permission}>
              Modify <ModifyPermission />
            </th>
            <th className={styles.permission}>
              Create Workbooks <WorkbooksPermission />
            </th>
            <th className={styles.permission}>
              Billing <BillingPermission />
            </th>
            <th className={styles.permission}>
              API <APIPermission />
            </th>
            <th className={styles.permission}>
              Manage <ManagePermission />
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(permissions || [])
            .concat(newPermissions)
            .map((p: PermissionType): React.ReactNode => renderPermission(p))}
        </tbody>
      </table>
      {deletedPermissionIds.map(
        (pId: string): React.ReactNode => renderDeletedPermission(pId),
      )}
      <Button
        bare
        variant="link"
        type="button"
        onClick={() => {
          addPermission();
        }}
      >
        Add Permission
      </Button>
    </div>
  );
}

export default RoleForm;
